import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DessemDataAnalisada, DessemCenario } from 'types/Relatorios/Dessem';

export interface RelatoriosDessemState {
  dataAnalisada: DessemDataAnalisada;
  cenario: DessemCenario;
  /**
   * `true` depois que a data/cenário iniciais vieram do `GET options` (`hidratarDessem`)
   * ou de uma escolha explícita do usuário (`setDessem*`). Até lá o `RelatoriosLayout`
   * segura as telas do DESSEM no spinner, para nenhuma consulta sair com a data provisória.
   */
  hidratado: boolean;
}

export interface DessemHidratacaoPayload {
  dataAnalisada: DessemDataAnalisada;
  cenario: DessemCenario;
}

/**
 * Hoje (relógio local) como `{ano, mes, dia}`, sem efeitos colaterais. O estado inicial
 * avalia uma vez, no load do módulo (placeholder até a hidratação); o fallback de
 * `useDessemHidratacao` chama de novo na hora. Não lê o relógio dentro dos reducers.
 */
export function hojeComoDataAnalisada(): DessemDataAnalisada {
  const hoje = new Date();
  return { ano: hoje.getFullYear(), mes: hoje.getMonth() + 1, dia: hoje.getDate() };
}

/**
 * Ontem como Data do Informe: o padrão do cliente quando o `GET options` falha. Hoje como D é a
 * previsão de amanhã, que só costuma estar publicada no fim do dia; o backend, no mesmo caso sem
 * dado, devolve D = ontem (13/10/2026).
 */
export function ontemComoDataAnalisada(): DessemDataAnalisada {
  const ontem = new Date();
  ontem.setDate(ontem.getDate() - 1);
  return { ano: ontem.getFullYear(), mes: ontem.getMonth() + 1, dia: ontem.getDate() };
}

/**
 * Tipo da action de logout (`reset` do slice `auth`, que `utils/logout.ts` despacha junto com
 * o `resetApiState`). Fica como string, e não como o action creator importado de
 * `redux/reducers/authReducer`: testes que mockam esse módulo com `reset: jest.fn()` (sem
 * `.type`) derrubariam o `builder.addCase` ("empty action type") ao montar o store. O teste
 * do reducer despacha o creator real e prende esta string ao slice `auth`.
 */
const AUTH_RESET_TYPE = 'auth/reset';
/** Tipo da action de login (`setAuthState` do slice `auth`): mesma razão de ser string. */
const AUTH_LOGIN_TYPE = 'auth/setAuthState';

const initialState: RelatoriosDessemState = {
  dataAnalisada: hojeComoDataAnalisada(),
  cenario: 'early',
  hidratado: false,
};

const relatoriosDessemSlice = createSlice({
  name: 'relatoriosDessem',
  initialState,
  reducers: {
    // Escolha explícita do usuário vence uma hidratação atrasada: marca `hidratado`, e o
    // `hidratarDessem` que chegar depois vira no-op.
    setDessemDataAnalisada: (state, action: PayloadAction<DessemDataAnalisada>) => {
      state.dataAnalisada = action.payload;
      state.hidratado = true;
    },
    setDessemCenario: (state, action: PayloadAction<DessemCenario>) => {
      state.cenario = action.payload;
      state.hidratado = true;
    },
    // Só a primeira hidratação vale: o `GET options` define a data/cenário de abertura uma
    // vez por sessão; depois, só o usuário muda (ou o `reset` / o logout).
    hidratarDessem: (state, action: PayloadAction<DessemHidratacaoPayload>) => {
      if (state.hidratado) return;
      state.dataAnalisada = action.payload.dataAnalisada;
      state.cenario = action.payload.cenario;
      state.hidratado = true;
    },
    reset: () => initialState,
  },
  // Logout e login encerram a sessão de hidratação. O slice não é persistido, mas a navegação
  // pós-login é SPA (sem reload): sem isto, o login seguinte na mesma aba — sessão expirada,
  // outro usuário, ou /signin aberto já logado (não passa pelo logout) — abriria na data da
  // hidratação anterior e não consultaria o options. O refresh do JWT (`updateAuthState`)
  // não entra.
  extraReducers: (builder) => {
    builder.addCase(AUTH_RESET_TYPE, () => initialState);
    builder.addCase(AUTH_LOGIN_TYPE, () => initialState);
  },
});

export const {
  setDessemDataAnalisada, setDessemCenario, hidratarDessem, reset,
} = relatoriosDessemSlice.actions;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dataDoSlice = (state: any): DessemDataAnalisada => state.relatoriosDessem.dataAnalisada;
/**
 * Memorizado: devolve o mesmo objeto enquanto a data não muda. O `useSelector` compara por
 * referência, e um objeto novo a cada chamada desenharia de novo, a cada ação do Redux (RTK Query,
 * polling), toda tela que lê a data.
 */
export const selectDessemDataAnalisada = createSelector(
  [
    (state: unknown) => dataDoSlice(state).ano,
    (state: unknown) => dataDoSlice(state).mes,
    (state: unknown) => dataDoSlice(state).dia,
  ],
  (ano, mes, dia): DessemDataAnalisada => ({ ano, mes, dia }),
);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectDessemCenario = (state: any): DessemCenario => (
  state.relatoriosDessem.cenario
);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectDessemHidratado = (state: any): boolean => (
  state.relatoriosDessem.hidratado
);

export default relatoriosDessemSlice.reducer;
